/* eslint-disable import/no-cycle */
import { Util } from '@libs/utilities/util';

import { IAmazonCompositeKeywordData, IAmazonOrganicRank, IAmazonProductRank } from '../../../../constants/research';
import { AmazonSvEvaluatorService } from '../search-volume/amazon-sv-evaluator.service';
import { GoogleSvEvaluatorService } from '../search-volume/google-sv-evaluator.service';
import { NicheEvaluatorService } from '../niche-evaluator.service';


export class RankClaimScoreEvaluatorService {

	private readonly MONTHS_PER_WEEK: number = 4.35;
	private readonly productRanksByKeyword: Map<string, IAmazonProductRank[]> = new Map<string, IAmazonProductRank[]>();
	private readonly amazonSearchVolumeByKeyword: Map<string, number> = new Map<string, number>();


	constructor(
		private nicheEvaluatorService	: NicheEvaluatorService,
		private amazonSvEvaluatorService: AmazonSvEvaluatorService,
		private googleSvEvaluatorService: GoogleSvEvaluatorService
	) {}


	/**------------------------------------------------------
	 * Product-rank context
	 */
	setProductRanks(keywordData: IAmazonCompositeKeywordData, productRanks: IAmazonProductRank[]): void {
		const keywordKey: string = this.keywordKey(keywordData);
		this.productRanksByKeyword.set(keywordKey, productRanks);
		this.amazonSearchVolumeByKeyword.delete(keywordKey);
	}

	clearProductRanks(): void {
		this.productRanksByKeyword.clear();
		this.amazonSearchVolumeByKeyword.clear();
	}


	/**------------------------------------------------------
	 * Positioning scores
	 */
	opportunityScore(keywordData: IAmazonCompositeKeywordData): number {
		const demandScore		: number = this.logNormalize(this.amazonSearchVolume(keywordData), 100, 100000);
		const trendScore		: number = this.trendScore(keywordData);
		const supplyPressure	: number = this.supplyDemandPressure(keywordData);
		const competitorStrength: number = this.competitorStrength(keywordData);

		return Math.round(100 * (
			demandScore * 0.40
			+ trendScore * 0.20
			+ (1 - supplyPressure) * 0.20
			+ (1 - competitorStrength) * 0.20
		));
	}

	adsPressure(keywordData: IAmazonCompositeKeywordData): number {
		const observedProducts: number = this.observedProductCount(keywordData);
		const sponsoredProducts: number = new Set(keywordData.amazon.competition.asinLists.sponsored || []).size;
		const sponsoredShare: number = observedProducts > 0 ? this.clamp(sponsoredProducts / observedProducts, 0, 1) : 0;

		return Math.round(100 * (
			sponsoredShare * 0.45
			+ this.competitorStrength(keywordData) * 0.30
			+ this.supplyDemandPressure(keywordData) * 0.25
		));
	}

	/** Legacy method name kept for the existing dataset mapping and sorting. */
	salesFactorPage1(keywordData: IAmazonCompositeKeywordData): number {
		return this.opportunityScore(keywordData);
	}

	/** Existing percentage-based consumers expect a value between zero and one. */
	adsRequirement(keywordData: IAmazonCompositeKeywordData): number {
		return this.adsPressure(keywordData) / 100;
	}


	/**------------------------------------------------------
	 * Sales potential
	 */
	expectedSalesPage1Range(keywordData: IAmazonCompositeKeywordData): IRankPositioningRange {
		return this.salesPotentialRange(keywordData, 0.0006, 0.0032, this.rankClaimPage1PerWeekRange(keywordData));
	}

	expectedSalesTop3Range(keywordData: IAmazonCompositeKeywordData): IRankPositioningRange {
		return this.salesPotentialRange(keywordData, 0.00225, 0.012, this.rankClaimTop3PerWeekRange(keywordData));
	}

	expectedSalesPage1(keywordData: IAmazonCompositeKeywordData): number {
		return this.rangeMidpoint(this.expectedSalesPage1Range(keywordData));
	}

	expectedSalesTop3(keywordData: IAmazonCompositeKeywordData): number {
		return this.rangeMidpoint(this.expectedSalesTop3Range(keywordData));
	}


	/**------------------------------------------------------
	 * Ranking effort
	 */
	rankClaimPage1PerWeekRange(keywordData: IAmazonCompositeKeywordData): IRankPositioningRange {
		const rankedSales: IRankedProductSales[] = this.rankedProductSales(keywordData).filter((product: IRankedProductSales) => product.rank <= 45);
		let boundarySales: number[] = rankedSales.filter((product: IRankedProductSales) => product.rank >= 31).map((product: IRankedProductSales) => product.monthlySales);

		if (boundarySales.length < 3 && rankedSales.length > 0) {
			const lowerThirdIndex: number = Math.floor(rankedSales.length * 2 / 3);
			boundarySales = rankedSales.slice(lowerThirdIndex).map((product: IRankedProductSales) => product.monthlySales);
		}

		return this.weeklyRangeFromObservedSales(keywordData, boundarySales, 1, 0.00025, 0.0015);
	}

	rankClaimTop3PerWeekRange(keywordData: IAmazonCompositeKeywordData): IRankPositioningRange {
		const rankedSales: IRankedProductSales[] = this.rankedProductSales(keywordData);
		let topSales: number[] = rankedSales.filter((product: IRankedProductSales) => product.rank <= 3).map((product: IRankedProductSales) => product.monthlySales);
		if (topSales.length < 2) topSales = rankedSales.slice(0, 5).map((product: IRankedProductSales) => product.monthlySales);

		const top3Range: IRankPositioningRange = this.weeklyRangeFromObservedSales(keywordData, topSales, this.top3FallbackFactor(keywordData), 0.0015, 0.0075);
		const page1Range: IRankPositioningRange = this.rankClaimPage1PerWeekRange(keywordData);
		return this.normalizeRange({
			min: Math.max(top3Range.min, page1Range.min),
			max: Math.max(top3Range.max, page1Range.max)
		});
	}

	rankClaimPage1PerWeek(keywordData: IAmazonCompositeKeywordData): number {
		return this.rangeMidpoint(this.rankClaimPage1PerWeekRange(keywordData));
	}

	rankClaimTop3PerWeek(keywordData: IAmazonCompositeKeywordData): number {
		return this.rangeMidpoint(this.rankClaimTop3PerWeekRange(keywordData));
	}


	/**------------------------------------------------------
	 * Calculation helpers
	 */
	private weeklyRangeFromObservedSales(
		keywordData	: IAmazonCompositeKeywordData,
		monthlySales	: number[],
		fallbackFactor	: number,
		demandLowRate	: number,
		demandHighRate	: number
	): IRankPositioningRange {
		let monthlyRange: IRankPositioningRange;

		if (monthlySales.length > 0) {
			monthlyRange = this.quantileRange(monthlySales);
		} else {
			const reviews: number = Math.max(
				keywordData.amazon.competition.reviews.count.page1 || 0,
				keywordData.amazon.competition.reviews.count.avg || 0
			);
			const estimatedSales: number = Math.max(0, this.nicheEvaluatorService.salesEstimation(keywordData.marketplace).salesPerMonthMba(reviews));
			monthlyRange = {
				min: estimatedSales * 0.80 * fallbackFactor,
				max: estimatedSales * 1.20 * fallbackFactor
			};
		}

		const amazonSearchVolume: number = this.amazonSearchVolume(keywordData);
		const trendMultiplier: number = this.clamp(1 + this.threeMonthTrend(keywordData) / 100 * 0.15, 0.75, 1.25);
		const demandRange: IRankPositioningRange = {
			min: amazonSearchVolume * demandLowRate * trendMultiplier,
			max: amazonSearchVolume * demandHighRate * trendMultiplier
		};

		return this.normalizeRange({
			min: Math.max(monthlyRange.min, demandRange.min) / this.MONTHS_PER_WEEK,
			max: Math.max(monthlyRange.max, demandRange.max) / this.MONTHS_PER_WEEK
		}, true);
	}

	private salesPotentialRange(keywordData: IAmazonCompositeKeywordData, lowRate: number, highRate: number, rankingEffort: IRankPositioningRange): IRankPositioningRange {
		const amazonSearchVolume: number = this.amazonSearchVolume(keywordData);
		const trendMultiplier: number = this.clamp(1 + this.threeMonthTrend(keywordData) / 100 * 0.15, 0.75, 1.25);
		const observedMonthlyFloor: IRankPositioningRange = {
			min: rankingEffort.min * this.MONTHS_PER_WEEK * 0.75,
			max: rankingEffort.max * this.MONTHS_PER_WEEK * 1.10
		};

		if (amazonSearchVolume <= 0) return this.normalizeRange(observedMonthlyFloor, true);

		return this.normalizeRange({
			min: Math.max(amazonSearchVolume * lowRate * trendMultiplier, observedMonthlyFloor.min),
			max: Math.max(amazonSearchVolume * highRate * trendMultiplier, observedMonthlyFloor.max)
		}, true);
	}

	private rankedProductSales(keywordData: IAmazonCompositeKeywordData): IRankedProductSales[] {
		const productRanks: IAmazonProductRank[] = this.productRanksByKeyword.get(this.keywordKey(keywordData)) || [];
		const normalizedKeyword: string = this.normalizeKeyword(keywordData.keyword);

		return productRanks
			.map((product: IAmazonProductRank) => {
				const keywordRanks: IAmazonOrganicRank[] = (product.organicRanks || [])
					.filter((rank: IAmazonOrganicRank) => this.normalizeKeyword(rank.keyword) === normalizedKeyword && Util.Number.isPositive(rank.rank))
					.sort((rank1: IAmazonOrganicRank, rank2: IAmazonOrganicRank) => new Date(rank2.createdAt).getTime() - new Date(rank1.createdAt).getTime());
				const latestRank: IAmazonOrganicRank = keywordRanks[0];
				if (!latestRank) return null;

				return {
					rank		: latestRank.rank,
					monthlySales: Math.max(0, this.nicheEvaluatorService.salesEstimation(keywordData.marketplace).salesPerMonthMba(product.reviews?.count || 0))
				};
			})
			.filter((product: IRankedProductSales | null): product is IRankedProductSales => Util.Basic.isDefined(product))
			.sort((product1: IRankedProductSales, product2: IRankedProductSales) => product1.rank - product2.rank);
	}

	private quantileRange(values: number[]): IRankPositioningRange {
		const sortedValues: number[] = values.filter((value: number) => Number.isFinite(value) && value >= 0).sort((value1: number, value2: number) => value1 - value2);
		if (sortedValues.length === 0) return { min: 0, max: 0 };
		if (sortedValues.length === 1) return { min: sortedValues[0] * 0.85, max: sortedValues[0] * 1.15 };

		return {
			min: this.quantile(sortedValues, 0.25),
			max: this.quantile(sortedValues, 0.75)
		};
	}

	private quantile(sortedValues: number[], percentile: number): number {
		const position: number = (sortedValues.length - 1) * percentile;
		const lowerIndex: number = Math.floor(position);
		const fraction: number = position - lowerIndex;
		return typeof sortedValues[lowerIndex + 1] === 'undefined'
			? sortedValues[lowerIndex]
			: sortedValues[lowerIndex] + fraction * (sortedValues[lowerIndex + 1] - sortedValues[lowerIndex]);
	}

	private amazonSearchVolume(keywordData: IAmazonCompositeKeywordData): number {
		const keywordKey: string = this.keywordKey(keywordData);
		const cachedSearchVolume = this.amazonSearchVolumeByKeyword.get(keywordKey);
		if (typeof cachedSearchVolume !== 'undefined') return cachedSearchVolume;

		try {
			const searchVolume: number = this.amazonSvEvaluatorService.current(keywordData);
			const normalizedSearchVolume: number = Number.isFinite(searchVolume) ? Math.max(0, searchVolume) : 0;
			this.amazonSearchVolumeByKeyword.set(keywordKey, normalizedSearchVolume);
			return normalizedSearchVolume;
		} catch (_) {
			this.amazonSearchVolumeByKeyword.set(keywordKey, 0);
			return 0;
		}
	}

	private threeMonthTrend(keywordData: IAmazonCompositeKeywordData): number {
		try {
			const trend: number = this.googleSvEvaluatorService.trends(keywordData).trend3Month;
			return Number.isFinite(trend) ? trend : 0;
		} catch (_) {
			return 0;
		}
	}

	private trendScore(keywordData: IAmazonCompositeKeywordData): number {
		return this.clamp(0.5 + this.threeMonthTrend(keywordData) / 200, 0, 1);
	}

	private supplyDemandPressure(keywordData: IAmazonCompositeKeywordData): number {
		const totalProducts: number = Math.max(0, keywordData.amazon.competition.totalProducts || 0);
		const supplyDemandRatio: number = totalProducts / Math.max(this.amazonSearchVolume(keywordData), 1);
		return this.logNormalize(supplyDemandRatio, 0.5, 100);
	}

	private competitorStrength(keywordData: IAmazonCompositeKeywordData): number {
		return this.logNormalize(Math.max(0, keywordData.amazon.competition.reviews.count.page1 || 0), 5, 1000);
	}

	private observedProductCount(keywordData: IAmazonCompositeKeywordData): number {
		return new Set([
			...keywordData.amazon.competition.asinLists.general || [],
			...keywordData.amazon.competition.asinLists.clothing || [],
			...keywordData.amazon.competition.asinLists.book || []
		]).size;
	}

	private top3FallbackFactor(keywordData: IAmazonCompositeKeywordData): number {
		return 1.5 + this.competitorStrength(keywordData) * 1.5 + this.adsPressure(keywordData) / 100;
	}

	private rangeMidpoint(range: IRankPositioningRange): number {
		return Math.round((range.min + range.max) / 2);
	}

	private normalizeRange(range: IRankPositioningRange, ceil: boolean = false): IRankPositioningRange {
		const normalize: (value: number) => number = (value: number): number => {
			if (!Number.isFinite(value) || value <= 0) return 0;
			return ceil ? Math.max(1, Math.ceil(value)) : Math.max(0, Math.round(value));
		};
		const min: number = normalize(Math.min(range.min, range.max));
		const max: number = normalize(Math.max(range.min, range.max));
		return { min, max: Math.max(min, max) };
	}

	private logNormalize(value: number, min: number, max: number): number {
		if (!Number.isFinite(value) || value <= min) return 0;
		if (value >= max) return 1;
		return this.clamp((Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min)), 0, 1);
	}

	private clamp(value: number, min: number, max: number): number {
		return Math.min(max, Math.max(min, value));
	}

	private keywordKey(keywordData: IAmazonCompositeKeywordData): string {
		return `${keywordData.marketplace}:${this.normalizeKeyword(keywordData.keyword)}`;
	}

	private normalizeKeyword(keyword: string): string {
		return (keyword || '').trim().toLowerCase();
	}
}


export interface IRankPositioningRange {
	min: number;
	max: number;
}

interface IRankedProductSales {
	rank		: number;
	monthlySales: number;
}
